import { useMemo, useState } from 'react';
import { ChevronRight, ChevronDown, File, Folder, FolderOpen } from 'lucide-react';
import './SkillFileTree.css';

function buildTree(files) {
  const root = { name: '', path: '', children: {}, isDir: true };
  const list = Array.isArray(files) ? files : [];
  for (const rel of list) {
    if (!rel) continue;
    const parts = String(rel).split('/');
    let node = root;
    let acc = '';
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      acc = acc ? `${acc}/${part}` : part;
      const isLeaf = i === parts.length - 1;
      if (!node.children[part]) {
        node.children[part] = {
          name: part,
          path: acc,
          children: {},
          isDir: !isLeaf,
        };
      }
      if (!isLeaf) {
        node.children[part].isDir = true;
      }
      node = node.children[part];
    }
  }
  return root;
}

function sortNodes(nodeMap) {
  const arr = Object.values(nodeMap);
  arr.sort((a, b) => {
    if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  return arr;
}

function TreeNode({ node, depth, expanded, toggle, activePath, onPick }) {
  const isOpen = expanded[node.path] !== false;
  const isActive = activePath === node.path;

  if (node.isDir) {
    const kids = sortNodes(node.children);
    return (
      <div className="sft-node">
        <button
          type="button"
          className="sft-row sft-row-dir"
          style={{ paddingLeft: `${8 + depth * 12}px` }}
          onClick={() => toggle(node.path)}
        >
          <span className="sft-chevron">
            {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </span>
          <span className="sft-icon">
            {isOpen ? <FolderOpen size={13} /> : <Folder size={13} />}
          </span>
          <span className="sft-name">{node.name}</span>
        </button>
        {isOpen && (
          <div className="sft-children">
            {kids.map((c) => (
              <TreeNode
                key={c.path}
                node={c}
                depth={depth + 1}
                expanded={expanded}
                toggle={toggle}
                activePath={activePath}
                onPick={onPick}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`sft-row sft-row-file ${isActive ? 'active' : ''}`}
      style={{ paddingLeft: `${8 + depth * 12}px` }}
      onClick={() => onPick(node.path)}
      title={node.path}
    >
      <span className="sft-chevron sft-chevron-spacer" />
      <span className="sft-icon"><File size={13} /></span>
      <span className="sft-name">{node.name}</span>
    </button>
  );
}

export default function SkillFileTree({ files, activePath, onPick }) {
  const tree = useMemo(() => buildTree(files), [files]);
  const [expanded, setExpanded] = useState({});

  const toggle = (path) => {
    setExpanded((prev) => ({ ...prev, [path]: prev[path] === false }));
  };

  const rootNodes = sortNodes(tree.children);

  return (
    <div className="sft">
      <div className="sft-header">Files</div>
      <div className="sft-list">
        {rootNodes.length === 0 ? (
          <div className="sft-empty">No files</div>
        ) : (
          rootNodes.map((c) => (
            <TreeNode
              key={c.path}
              node={c}
              depth={0}
              expanded={expanded}
              toggle={toggle}
              activePath={activePath}
              onPick={onPick}
            />
          ))
        )}
      </div>
    </div>
  );
}
